import React, { useEffect, useState } from "react";

import {
  OrderItemBulkUpsert,
  OrderItemBulkUpsertRequest,
  OrderItemResponse,
} from "@/api";

import OrderItemsView from "./OrderItemsView";
import OrderItemsEdit from "./OrderItemsEdit";

import {
  AdminOrderUpdateService,
} from "../../service/adminOrderUpdateServices";

interface OrderItemsSectionProps {
  orderId: number;

  items: OrderItemResponse[];

  onChange: () => void;
}

export default function OrderItemsSection({
  orderId,
  items,
  onChange,
}: OrderItemsSectionProps) {
  const [editing, setEditing] = useState(false);

  const [draft, setDraft] =
    useState<OrderItemResponse[]>(items);

  const [saving, setSaving] = useState(false);

  const [error, setError] =
    useState<string | null>(null);

  /*
   * --------------------------------------------------
   * EDIT
   * --------------------------------------------------
   */

  const handleEdit = () => {
    setDraft(items);
    setError(null);
    setEditing(true);
  };

  /*
   * --------------------------------------------------
   * SAVE
   * --------------------------------------------------
   */

const handleSave = async () => {
  try {
    setSaving(true);
    setError(null);

    const items: OrderItemBulkUpsert[] =
      draft.map((item) => {
        if (
          item.application_id === null ||
          item.application_id === undefined
        ) {
          throw new Error(
            `Order item ${item.id ?? ""} has no application.`,
          );
        }

        return {
          id: item.id ?? null,
          application_id: item.application_id,
          quantity: Number(item.quantity ?? 1),
          discount_percentage:
            item.discount_percentage ?? undefined,
        };
      });

    console.log(
      "ORDER ITEM UPDATE REQUEST PAYLOAD:",
      JSON.stringify(items),
    );

    const payload: OrderItemBulkUpsertRequest = {
      items,
    };

    console.log(
      "BULK UPSERT PAYLOAD:",
      JSON.stringify(payload, null, 2),
    );

    await AdminOrderUpdateService.upsert_order_items(
      orderId,
      payload,
    );

    /*
     * Refresh the authoritative AdminOrderResponse.
     *
     * The parent callback is responsible for fetching the
     * latest order, including items, commercials and payment
     * schedules.
     */
    await onChange();

    /*
     * Only leave edit mode after the save and refresh
     * have completed successfully.
     */
    setEditing(false);

  } catch (err) {
    console.error(
      "Failed to update order items",
      err,
    );

    setError(
      err instanceof Error
        ? err.message
        : "Failed to update order items.",
    );
  } finally {
    setSaving(false);
  }
};

  /*
   * --------------------------------------------------
   * CANCEL
   * --------------------------------------------------
   */

  const handleCancel = () => {
    setDraft(items);
    setError(null);
    setEditing(false);
  };

  /*
   * --------------------------------------------------
   * SYNCHRONIZE FROM PARENT
   *
   * Parent refreshes the entire AdminOrderResponse
   * after a successful save.
   * --------------------------------------------------
   */

  useEffect(() => {
    if (!editing) {
      setDraft(items);
    }
  }, [items, editing]);

  return (
    <section className="rounded-xl border bg-white p-6">

      {/* ==================================================
          EDIT MODE
          ================================================== */}

      {editing ? (
        <>
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                Order Items
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Edit the items included in this order.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="rounded-md border px-3 py-2 text-sm disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="rounded-md bg-black px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                {saving
                  ? "Saving..."
                  : "Save"}
              </button>
            </div>
          </div>

          <OrderItemsEdit
            items={draft}
            onChange={setDraft}
          />

          {error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </>
      ) : (

        /* ==================================================
           VIEW MODE
           ================================================== */

        <>
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                Order Items
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Items included in this order.
              </p>
            </div>

            <button
              type="button"
              onClick={handleEdit}
              className="rounded-md border px-3 py-2 text-sm font-medium"
            >
              Edit
            </button>
          </div>

          <OrderItemsView
            items={items}
          />
        </>
      )}
    </section>
  );
}